import { mapValues } from 'lodash';

const lazy = <T, K extends keyof T>(asyncComponent: () => Promise<T>, key: K | 'default' = 'default') => ({
  lazy: async () => ({ Component: (await asyncComponent())[key as K] }),
});

type RouteOption<T> = {
  lazy?: any;
  pageId?: T;
};

const transformRouteOptions = <T>(options: Record<string, RouteOption<T>>) =>
  mapValues(options, ({ pageId, ...rest }) => ({
    ...rest,
    handle: {
      pageId,
    },
  }));

const rawLazyRoutes = {
  HomePreview: { ...lazy(() => import('./web/routes/home/home')) },
  Home: { ...lazy(() => import('./web/routes/hero-preview')), pageId: 'home' as const },
  SignUp: { ...lazy(() => import('./web/routes/sign-up')), pageId: 'sign-up' as const },
  SignIn: { ...lazy(() => import('./web/routes/sign-in')), pageId: 'sign-in' as const },
  SignOut: { ...lazy(() => import('./web/routes/sign-out')), pageId: 'sign-out' as const },
  RequireAuth: { ...lazy(() => import('utils/react/require-auth-route'), 'RequireAuth') },
  RequireOrg: { ...lazy(() => import('utils/react/require-org-route'), 'RequireOrg') },
  RequireAppOnboarding: {
    ...lazy(
      () => import('pages/vo/vo-react/components/app-onboarding/app-onboarding'),
      'RequireAppOnboarding',
    ),
  },
  Pricing: { ...lazy(() => import('./web/routes/pricing')), pageId: 'pricing' as const },
  OpenDesktopApp: {
    ...lazy(() => import('./web/routes/open-desktop-app'), 'OpenDesktopApp'),
    pageId: 'open-desktop-app' as const,
  },
  DownloadApp: {
    ...lazy(() => import('./web/routes/download-app'), 'DownloadApp'),
    pageId: 'download-app' as const,
  },
  DownloadAppPlatform: {
    ...lazy(() => import('./web/routes/download-app'), 'DownloadAppPlatform'),
    pageId: 'download-app-platform' as const,
  },
  AppShell: {
    lazy: async () => {
      const [AppShell, AppError] = await Promise.all([import('./app-shell'), import('./app-error')]);
      return { Component: AppShell.default, ErrorBoundary: AppError.default };
    },
  },
  DashboardShell: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/dashboard'), 'DashboardShell'),
  },
  App: { ...lazy(() => import('pages/vo/vo-react/app'), 'App') },
  WebShell: { ...lazy(() => import('./web/web-shell')) },
  WaitForBrowserSignin: {
    ...lazy(
      () => import('pages/vo/vo-react/components/dashboard/wait-for-browser-signin'),
      'WaitForBrowserSignin',
    ),
    pageId: 'wait-for-browser-signin' as const,
  },
  DashboardHomePage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/home'), 'DashboardHomePage'),
    pageId: 'dashboard-home' as const,
  },
  WidgetSettingsPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/widget-settings'), 'WidgetSettingsPage'),
    pageId: 'widget-settings' as const,
  },
  OrganizationsPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/organizations'), 'OrganizationsPage'),
    pageId: 'organizations' as const,
  },
  CreateOrJoinOrgPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/create-or-join-org'), 'CreateOrJoinOrgPage'),
    pageId: 'create-or-join-org' as const,
  },
  OrgWidgetSettingsPage: {
    ...lazy(
      () => import('pages/vo/vo-react/components/dashboard/org-widget-settings'),
      'OrgWidgetSettingsPage',
    ),
    pageId: 'org-widget-settings' as const,
  },
  OrgDetailsPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/org-details'), 'OrgDetailsPage'),
    pageId: 'org-details' as const,
  },
  OrgMembersPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/org-members'), 'OrgMembersPage'),
    pageId: 'org-members' as const,
  },
  OrgTeamsPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/org-teams'), 'OrgTeamsPage'),
    pageId: 'org-teams' as const,
  },
  OrgSettingsPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/org-settings'), 'OrgSettingsPage'),
    pageId: 'org-settings' as const,
  },
  BallparkNetcodeTest: {
    ...lazy(() => {
      const url = '/apps/ballpark/netcode-test.js';
      return import(/* @vite-ignore */ url);
    }, 'BallparkNetcodeTest'),
    pageId: 'ballpark-netcode-test' as const,
  },
  PileVisualizer: {
    ...lazy(() => import('pages/vo/vo-react/components/dev/pile-visualizer'), 'PileVisualizer'),
    pageId: 'pile-visualizer' as const,
  },
  DashboardNewOrgPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/new-org'), 'NewOrgPage'),
    pageId: 'new-org' as const,
  },
  DashboardNewManualOrgPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/new-org'), 'NewManualOrgPage'),
    pageId: 'new-manual-org' as const,
  },
  DashboardManualOrgCreatedPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/new-org'), 'ManualOrgCreatedPage'),
    pageId: 'manual-org-created' as const,
  },
  DashboardNewSlackOrgPage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/new-org'), 'NewSlackOrgPage'),
  },
  DashboardOrgInvitePage: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/invite'), 'DashboardOrgInvite'),
    pageId: 'org-invite' as const,
  },
  DashboardAddOrEditTeamPage: {
    ...lazy(
      () => import('pages/vo/vo-react/components/dashboard/add-or-edit-team'),
      'DashboardAddOrEditTeamPage',
    ),
    pageId: 'add-or-edit-team' as const,
  },
  DashboardDeleteTeamConfirmationPage: {
    ...lazy(
      () => import('pages/vo/vo-react/components/dashboard/add-or-edit-team'),
      'DashboardDeleteTeamConfirmationPage',
    ),
    pageId: 'delete-team-confirmation' as const,
  },
  DashboardLeaveOrgConfirmationPage: {
    ...lazy(
      () => import('pages/vo/vo-react/components/dashboard/org-settings'),
      'DashboardLeaveOrgConfirmationPage',
    ),
    pageId: 'leave-org-confirmation' as const,
  },
  TeamSettings: {
    ...lazy(() => import('pages/vo/vo-react/components/details/team-settings'), 'TeamSettings'),
    pageId: 'team-settings' as const,
  },
  AllTeams: {
    ...lazy(() => import('pages/vo/vo-react/components/dashboard/all-teams'), 'DashboardAllTeamsPage'),
    pageId: 'all-teams' as const,
  },
  PeopleSettings: {
    ...lazy(() => import('pages/vo/vo-react/components/details/people-settings'), 'PeopleSettings'),
  },
  AllPeople: {
    ...lazy(() => import('pages/vo/vo-react/components/details/all-people'), 'AllPeople'),
    pageId: 'all-people' as const,
  },
  AppOnboarding: {
    ...lazy(() => import('pages/vo/vo-react/components/app-onboarding/app-onboarding'), 'AppOnboarding'),
    pageId: 'app-onboarding' as const,
  },
  VerifyEmail: {
    ...lazy(() => import('./web/routes/verify-email')),
    pageId: 'verify-email' as const,
  },
  GoogleLoginRedirect: {
    ...lazy(() => import('./web/routes/google-login-redirect')),
    pageId: 'google-login-redirect' as const,
  },
  OktaLoginRedirect: {
    ...lazy(() => import('./web/routes/okta-login-redirect')),
    pageId: 'okta-login-redirect' as const,
  },
  OktaLinkAccount: {
    ...lazy(() => import('./web/routes/okta-link-account')),
    pageId: 'okta-link-account' as const,
  },
  LinkAccount: {
    ...lazy(() => import('./web/routes/link-account')),
    pageId: 'link-account' as const,
  },
  LinkWithSlack: {
    ...lazy(() => import('./web/routes/link-with-slack'), 'LinkWithSlack'),
    pageId: 'link-with-slack' as const,
  },
  ResetPassword: {
    ...lazy(() => import('./web/routes/reset-password')),
    pageId: 'reset-password' as const,
  },
  AcceptOrgInvite: {
    ...lazy(() => import('pages/vo/vo-react/web/routes/accept-org-invite'), 'AcceptOrgInvite'),
  },
};
export const lazyRoutes = transformRouteOptions(rawLazyRoutes);
export type PageId = Exclude<(typeof lazyRoutes)[keyof typeof lazyRoutes]['handle']['pageId'], undefined>;
